import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { getScrollTop } from '@theme/scroll-container';

/**
 * Scroll-linked landing parent block. Drives the entry reveals with an
 * IntersectionObserver and feeds the scroll-linked CSS custom properties
 * (--bb-hero, --bb-story, --bb-mx, --bb-p) from a single rAF loop that only
 * runs while the block is near the viewport. All properties are set on the
 * element itself (and inherited by the child blocks) so nothing leaks outside
 * the block.
 *
 * Content lives in child blocks that the theme editor can add, remove and
 * reorder without reconnecting this element, so a MutationObserver re-caches
 * the drift/hero/story references and observes newly rendered reveal targets.
 */
class BillieLanding extends Component {
  /** @type {number | null} */
  #raf = null;
  /** @type {IntersectionObserver | null} */
  #revealObserver = null;
  /** @type {IntersectionObserver | null} */
  #screenObserver = null;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #revealFallback = null;
  #lastY = 0;
  #mx = 0;
  /** @type {HTMLElement[]} */
  #driftElements = [];
  /** @type {HTMLElement | null} */
  #hero = null;
  /** @type {HTMLElement | null} */
  #story = null;
  /** @type {Set<Element>} */
  #observedReveals = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#lastY = getScrollTop();
    this.#refreshTargets();
    this.#revealFallback = setTimeout(() => this.#showAllReveals(), 3000);

    // Child blocks re-render independently in the theme editor; keep our
    // element caches and reveal observation in sync with the live DOM.
    this.#mutationObserver = new MutationObserver(() => this.#refreshTargets());
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    if (!this.#motionEnabled) {
      this.classList.add('billie-landing--static');
      this.style.setProperty('--bb-hero', '0');
      this.style.setProperty('--bb-story', '1');
      this.style.setProperty('--bb-mx', '0');
      return;
    }

    // Only run the rAF loop while the block is near the viewport.
    this.#screenObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        if (entry.isIntersecting) {
          this.#start();
        } else {
          this.#stop();
        }
      },
      { rootMargin: '240px 0px' }
    );
    this.#screenObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#revealObserver?.disconnect();
    this.#screenObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    if (this.#revealFallback) clearTimeout(this.#revealFallback);
    this.#observedReveals.clear();
  }

  get #motionEnabled() {
    return this.dataset.motion !== 'false' && !prefersReducedMotion();
  }

  /**
   * Re-caches scroll-linked element references and observes any reveal targets
   * that are not yet observed (initial render and editor re-renders alike).
   */
  #refreshTargets() {
    this.#hero = this.querySelector('[data-bb-hero]');
    this.#story = this.querySelector('[data-bb-story]');
    this.#driftElements = Array.from(this.querySelectorAll('[data-bb-drift], [data-bb-driftx]'));
    this.#observeReveals();
  }

  #revealTargets() {
    return Array.from(
      this.querySelectorAll('[data-bb-reveal], [data-bb-reveal-group], [data-bb-clip], [data-bb-mask], [data-bb-rule]')
    );
  }

  #showAllReveals() {
    this.#revealTargets().forEach((el) => el.classList.add('billie-in'));
  }

  /**
   * Class-driven entry reveals so a re-render can never hide content, with a
   * timed fallback in case observation never fires.
   */
  #observeReveals() {
    if (!('IntersectionObserver' in window)) {
      this.#showAllReveals();
      return;
    }

    this.#revealObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('billie-in');
            this.#revealObserver?.unobserve(entry.target);
            this.#observedReveals.delete(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
    );

    for (const el of this.#revealTargets()) {
      if (el.classList.contains('billie-in') || this.#observedReveals.has(el)) continue;
      this.#observedReveals.add(el);
      this.#revealObserver.observe(el);
    }
  }

  #start() {
    if (this.#raf === null) this.#loop();
  }

  #stop() {
    if (this.#raf !== null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
  }

  #loop = () => {
    this.#raf = requestAnimationFrame(this.#loop);

    const viewportHeight = window.innerHeight || 800;
    const y = getScrollTop();

    if (this.#hero) {
      const rect = this.#hero.getBoundingClientRect();
      const progress = rect.height > 0 ? Math.min(1, Math.max(0, -rect.top / rect.height)) : 0;
      this.style.setProperty('--bb-hero', progress.toFixed(4));
    }

    if (this.#story) {
      const rect = this.#story.getBoundingClientRect();
      const progress = (viewportHeight * 0.92 - rect.top) / (viewportHeight * 0.78);
      this.style.setProperty('--bb-story', Math.max(0, Math.min(1, progress)).toFixed(4));
    }

    // Marquee reacts to scroll velocity, then eases back to rest.
    const dy = y - this.#lastY;
    this.#lastY = y;
    this.#mx = (this.#mx - dy * 1.5) * 0.9;
    if (Math.abs(this.#mx) < 0.05) this.#mx = 0;
    this.style.setProperty('--bb-mx', this.#mx.toFixed(2));

    for (const el of this.#driftElements) {
      const host = el.parentElement;
      if (!host) continue;
      const rect = host.getBoundingClientRect();
      if (rect.bottom < -240 || rect.top > viewportHeight + 240) continue;
      const progress = (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight;
      el.style.setProperty('--bb-p', (-progress).toFixed(4));
    }
  };
}

if (!customElements.get('billie-landing')) {
  customElements.define('billie-landing', BillieLanding);
}
